import React, { useState, useMemo } from "react";
import { TbSend, TbEye } from "react-icons/tb";
import { useEmailTemplates, useSendProspectReport } from "../../hooks/useProspecting";
import {
  PROSPECT_SUBJECT, PROSPECT_BODY, SUBJECT_TOKENS, BODY_TOKENS, fillTokens,
} from "../../lib/prospect";
import { Button, Field, Alert, Modal } from "../../components";
import "./Scanner.css";

// Send a prospecting report, for one scan.
//
// A dialog rather than a block on the scan form, because emailing is a decision you make
// AFTER reading the results. The old form asked for the prospect's address, subject and
// a nine-row body before you had any idea whether the scan was worth sending — and then
// ignored all of it, since the scan call never passed those fields anywhere. Every scan
// looked like it was going to email someone and none of them did.
//
// Opened from two places over the same props: straight after a scan, and from a history
// row. That is the whole reason it takes a `scan` rather than reading component state.
const SendProspectReportModal = ({ scan, reportTheme, onClose, onSent }) => {
  const { templates } = useEmailTemplates("prospect");
  const { send, sending } = useSendProspectReport();

  const [to, setTo] = useState(scan.prospectEmail || "");
  const [name, setName] = useState(scan.prospectName || "");
  const [company, setCompany] = useState(scan.prospectCompany || "");
  const [subject, setSubject] = useState(PROSPECT_SUBJECT);
  const [body, setBody] = useState(PROSPECT_BODY);
  const [templateId, setTemplateId] = useState("");
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const ctx = useMemo(
    () => ({ results: scan.results, domain: scan.domain, prospectName: name, prospectCompany: company }),
    [scan.results, scan.domain, name, company]
  );
  const resolved = useMemo(
    () => ({ subject: fillTokens(subject, ctx), body: fillTokens(body, ctx) }),
    [subject, body, ctx]
  );

  const pickTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => String(x.id) === String(id));
    setSubject(t ? (t.subject ?? "") : PROSPECT_SUBJECT);
    setBody(t ? (t.body ?? "") : PROSPECT_BODY);
  };

  const submit = async () => {
    if (!to.trim()) { setError("Add the prospect's email address."); return; }
    setError(""); setDone("");
    try {
      // The tokens are resolved here, not on the server: the server has no scan in hand
      // when it sends, only the text it is given.
      await send({
        scanId: scan.scanId, results: scan.results, domain: scan.domain,
        to: to.trim(), name, company,
        subject: resolved.subject, body: resolved.body,
        // The theme chosen for this report, not the operator's own interface — the
        // attachment is the prospect's copy and used to arrive dark whenever the sender
        // happened to be working in dark mode.
        theme: reportTheme || "light",
      });
      setDone(`Report sent to ${to.trim()}.`);
      await onSent?.();
    } catch (e) {
      setError(e.message || "Could not send the report.");
    }
  };

  if (preview) {
    return (
      <Modal wide title="Email preview" onClose={() => setPreview(false)}
             footer={<Button onClick={() => setPreview(false)}>Back to editing</Button>}>
        <Field label="To"><div className="v2-preview">{to || "—"}</div></Field>
        <Field label="Subject"><div className="v2-preview">{resolved.subject}</div></Field>
        <Field label="Message"><div className="v2-preview v2-preview--body">{resolved.body}</div></Field>
      </Modal>
    );
  }

  return (
    <Modal
      wide
      title={`Send the ${scan.domain} report`}
      onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={sending}>{done ? "Close" : "Cancel"}</Button>
        <Button style={{ marginLeft: "auto" }} onClick={() => setPreview(true)} disabled={sending}>
          <TbEye size={15} /> Preview
        </Button>
        <Button variant="primary" onClick={submit} disabled={sending || !to.trim()}>
          <TbSend size={15} /> {sending ? "Generating and sending…" : "Send report"}
        </Button>
      </>}
    >
      <Alert>{error}</Alert>
      {done && <Alert tone="success">{done}</Alert>}

      <div className="v2-scanform-row three">
        <Field label="Prospect email">
          <input className="v2-input" type="email" value={to} autoFocus
                 onChange={(e) => { setTo(e.target.value); setError(""); }} placeholder="name@example.com" />
        </Field>
        <Field label="Name">
          <input className="v2-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Company">
          <input className="v2-input" value={company} onChange={(e) => setCompany(e.target.value)} />
        </Field>
      </div>

      <Field label="Template">
        <select className="v2-input" value={templateId} onChange={(e) => pickTemplate(e.target.value)}>
          <option value="">Built-in default</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Field>

      <Field label="Subject" hint={`Use ${SUBJECT_TOKENS}`}>
        <input className="v2-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label="Message" hint={`Use ${BODY_TOKENS}`}>
        <textarea className="v2-input v2-longtext" rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>

      <span className="v2-hint">
        The report is attached as a PDF, generated in your browser when you send.
      </span>
    </Modal>
  );
};

export default SendProspectReportModal;
